<script setup lang="ts">
import { useFortnoxAuth } from '@/composeables/use-auth';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import { notificationService } from '@/services/notification/notification-service';
import { appHttpService } from '@/services/standard/http-service';
import { syncService } from '@/services/sync/sync-service';
import { PhDownloadSimple } from '@phosphor-icons/vue';
import { models } from 'cesg-shared';
import { computed, onActivated, onMounted, ref } from 'vue';

const isLoggedIn = entraAuthService.isLoggedIn;
const isInitialized = entraAuthService.isInitialized;
const activeAccount = entraAuthService.activeAccount;

const userName = computed(() => activeAccount.value?.name);
const entraUser = ref<object | null>(null);

const fortnoxAuth = useFortnoxAuth();
const fortnoxAuthenticated = fortnoxAuth.authenticated;
const fortnoxMe = fortnoxAuth.me;
const fortnoxLogin = fortnoxAuth.login;
const fortnoxLogout = fortnoxAuth.logout;

const fortnoxApiResponse = ref<models.GetSyncStatusResponse[] | null>(null);

const syncStatus = ref<models.GetSyncStatusResponse[] | null>(null);
const isSyncingFortnoxInvoiceFiles = ref(false);

onMounted(async () => {
  if (fortnoxAuthenticated.value) {
    syncStatus.value = await syncService.getStatus();
  }
});

onActivated(async () => {
  if (fortnoxAuthenticated.value) {
    syncStatus.value = await syncService.getStatus();
  }
});

const entraLogin = async () => {
  await entraAuthService.login();
};

const entraLogout = async () => {
  await entraAuthService.logout();
};

const callApiSecure = async () => {
  entraUser.value = null;
  const user = await appHttpService.request
    .url(`/entra/secure`)
    .get()
    .json<models.SessionUser>();
  entraUser.value = user;
};

const callApiSecureAdmin = async () => {
  entraUser.value = null;
  const user = await appHttpService.request
    .url(`/entra/secure/admin`)
    .get()
    .json<models.SessionUser>();
  entraUser.value = user;
};

const callApiSecurePermission = async () => {
  entraUser.value = null;
  const projectId = 'cmgfi4bie0003itnga8pjvj47'; // hardcoded!!
  const user = await appHttpService.request
    .url(`/entra/secure/permission/${projectId}`)
    .get()
    .json<models.SessionUser>();
  entraUser.value = user;
};

const callFortnoxApi = async () => {
  fortnoxApiResponse.value = await syncService.getStatus();
};

const sync = async (resouce: models.SyncResources) => {
  await syncService.syncResource(resouce);
};

const lastSynced = (resource: models.SyncResources) => {
  return syncStatus.value?.find((status) => status.resource === resource)
    ?.lastSync;
};

const callFortnoxSyncInvoiceFiles = async () => {
  isSyncingFortnoxInvoiceFiles.value = true;
  try {
    await syncService.syncFortnoxInvoiceFiles();
    notificationService.open({
      type: 'success',
      text: 'Sync of Fortnox invoice files done.',
    });
  } finally {
    isSyncingFortnoxInvoiceFiles.value = false;
  }
};
</script>

<template>
  <h1 class="is-size-4">Auth test</h1>

  <div v-if="!isInitialized">
    <p>Loading authentication...</p>
  </div>
  <div v-else>
    <div class="grid">
      <div class="cell">
        <h2 class="is-size-5">Microsoft EntraID login</h2>
        <section v-if="!isLoggedIn" class="">
          <p>Ej inloggad</p>
          <button class="button is-primary mt-4" @click="entraLogin">
            Logga in
          </button>
        </section>
        <section v-else>
          Inloggad som: <span>{{ userName }}</span>
          <div>
            <button class="button is-primary mt-4" @click="entraLogout">
              Logga ut
            </button>
          </div>
        </section>

        <section>
          <div class="buttons">
            <button class="button is-success mt-4" @click="callApiSecure">
              Api call secure
            </button>
            <button class="button is-success mt-4" @click="callApiSecureAdmin">
              Api call secure admin
            </button>
            <button
              class="button is-success mt-4"
              @click="callApiSecurePermission"
            >
              Api call secure permission
            </button>
          </div>

          <pre v-if="entraUser">
            {{ entraUser }}
          </pre>
        </section>

        <section
          class="p-4 mt-6 is-flex is-flex-direction-column is-align-items-start"
        >
          <h2 class="is-size-5">FORTNOX PORTING PROD</h2>

          <div>
            <button
              type="button"
              class="button is-primary mt-2"
              :class="{ 'is-loading': isSyncingFortnoxInvoiceFiles }"
              :disabled="isSyncingFortnoxInvoiceFiles"
              @click="callFortnoxSyncInvoiceFiles"
            >
              Synka fortnox faktura pdf
            </button>
          </div>
        </section>
      </div>

      <div class="cell">
        <h2 class="is-size-5">Fortnox login</h2>
        <section v-if="!fortnoxAuthenticated" class="">
          <p>Ej inloggad</p>
          <button class="button is-primary mt-4" @click="fortnoxLogin">
            Logga in
          </button>
        </section>
        <section v-else>
          Inloggad som:
          <span>{{ fortnoxMe?.name }}</span>
          <div>
            <button class="button is-primary mt-4" @click="fortnoxLogout">
              Logga ut
            </button>
          </div>
        </section>

        <section>
          <button class="button is-success mt-4" @click="callFortnoxApi">
            Api call
          </button>

          <pre v-if="fortnoxApiResponse">
            {{ fortnoxApiResponse }}
          </pre>
        </section>

        <section v-if="fortnoxAuthenticated">
          <h2 class="is-size-5">Fortnox sync</h2>
          <div class="fixed-grid has-3-cols" v-if="syncStatus">
            <div class="grid">
              <div class="cell">Accounts</div>
              <div class="cell">
                <button
                  class="button is-dark"
                  @click="sync(models.SyncResources.Accounts)"
                >
                  <PhDownloadSimple class="mr-2" :size="24" /> Synka
                </button>
              </div>
              <div class="cell">
                {{ lastSynced(models.SyncResources.Accounts) }}
              </div>

              <div class="cell">Customer invoices</div>
              <div class="cell">
                <button
                  class="button is-dark"
                  @click="sync(models.SyncResources.CustomerInvoices)"
                >
                  <PhDownloadSimple class="mr-2" :size="24" /> Synka
                </button>
              </div>
              <div class="cell">
                {{ lastSynced(models.SyncResources.CustomerInvoices) }}
              </div>

              <div class="cell">Supplier invoices</div>
              <div class="cell">
                <button
                  class="button is-dark"
                  @click="sync(models.SyncResources.SupplierInvoices)"
                >
                  <PhDownloadSimple class="mr-2" :size="24" /> Synka
                </button>
              </div>
              <div class="cell">
                {{ lastSynced(models.SyncResources.SupplierInvoices) }}
              </div>

              <div class="cell">Projects</div>
              <div class="cell">
                <button
                  class="button is-dark"
                  @click="sync(models.SyncResources.Projects)"
                >
                  <PhDownloadSimple class="mr-2" :size="24" /> Synka
                </button>
              </div>
              <div class="cell">
                {{ lastSynced(models.SyncResources.Projects) }}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
